import { Component, OnInit, OnDestroy, AfterViewInit, ViewChild, ElementRef, NgZone, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material';
import {
    Participant,
    RTVIClient,
    RTVIClientOptions,
    RTVIEvent,
} from '@pipecat-ai/client-js';
import { DailyTransport, DailyRTVIMessageType } from '@pipecat-ai/daily-transport';
import { SoundUtils } from './sound-utils';
import { InstantVoiceHelper } from './instant-voice-helper';
import { environment } from 'src/environments/environment';
import { AppService } from 'src/@core/services/app.service';
import { VocalLabsWebCallService } from 'src/@core/services/vocallabs/vocallabs-web-call.service';

@Component({
    selector: 'hour4u-admin-test-agent-dialog',
    templateUrl: './test-agent-dialog.component.html',
    styleUrls: ['./test-agent-dialog.component.scss'],
})
export class TestAgentDialogComponent implements OnInit, OnDestroy, AfterViewInit {
    @ViewChild('statusSpan', { static: false }) statusSpan: ElementRef<HTMLElement>;
    @ViewChild('bufferingAudioSpan', { static: false }) bufferingAudioSpan: ElementRef<HTMLElement>;
    @ViewChild('debugLog', { static: false }) debugLog: ElementRef<HTMLElement>;
    @ViewChild('botAudio', { static: false }) botAudio: ElementRef<HTMLAudioElement>;

    private rtviClient: RTVIClient;
    private startTime: number;
    public isConnected: boolean = false;
    public isAudioBuffering: boolean = false;
    private audioCheckInterval: any;
    private instantVoiceHelper: InstantVoiceHelper;
    isSpeaking: boolean;
    bars: number[] = [5, 10, 7, 3, 8]; // bar heights
    audioContext: AudioContext;
    analyser: AnalyserNode;
    source: MediaElementAudioSourceNode;
    animationFrameId: any;
    isConnecting = false;
    isDisconnecting = false;

    public dialogTitle: string = 'Test Agent';
    public agentType: string = 'test';
    private useVocalLabsWs: boolean = false;

    constructor(
        private dialogRef: MatDialogRef<TestAgentDialogComponent>,
        private ngZone: NgZone,
        @Inject(MAT_DIALOG_DATA) public data: any,
        private appService: AppService,
        private vocalLabsWebCall: VocalLabsWebCallService
    ) {
        console.log('TestAgentDialogComponent constructor called with data:', data);
        // Set dialog title from data if provided, otherwise use default
        this.dialogTitle = (data && data.dialogTitle) || 'Test Agent';
        this.agentType = (data && data.agentType) || 'test';
        this.useVocalLabsWs = !!(data && (data.vocallabsWsUrl || data.useVocallabs === true || data.callProvider === 'vocallabs'));
    }

    ngOnInit() {
        console.log('TestAgentDialogComponent ngOnInit called');
        this.log('Component initialized');
        try {
            if (this.useVocalLabsWs) {
                this.initializeVocalLabs();
            } else {
                this.initializeRTVIClient();
            }
        } catch (error) {
            this.log(`Error in ngOnInit: ${error}`);
            console.error('Error in ngOnInit:', error);
        }
    }

    ngAfterViewInit() {
        console.log('TestAgentDialogComponent ngAfterViewInit called');
        this.log('View initialized');
        const audio = this.botAudio.nativeElement;

        this.audioContext = new AudioContext();
        this.analyser = this.audioContext.createAnalyser();
        this.analyser.fftSize = 64;

        this.source = this.audioContext.createMediaElementSource(audio);
        this.source.connect(this.analyser);
        this.analyser.connect(this.audioContext.destination);

        audio.onplay = () => {
            this.audioContext.resume();
            this.animateBars();
            this.isSpeaking = true;
        };

        audio.onpause = audio.onended = () => {
            cancelAnimationFrame(this.animationFrameId);
            this.isSpeaking = false;
        };
    }
    animateBars() {
        const data = new Uint8Array(this.analyser.frequencyBinCount);
        const draw = () => {
            this.analyser.getByteFrequencyData(data);
            // Sample 5 values and normalize height
            this.bars = [0, 1, 2, 3, 4].map(i => Math.max(4, data[i] / 8));
            this.animationFrameId = requestAnimationFrame(draw);
        };
        draw();
    }

    ngOnDestroy() {
        console.log('TestAgentDialogComponent ngOnDestroy called');
        this.log('Component destroying');
        this.disconnect();
    }

    private initializeVocalLabs(): void {
        this.log('Initializing VocalLabs WebSocket call mode...');
        this.vocalLabsWebCall.onConnected(() => {
            this.ngZone.run(() => {
                this.updateStatus('Connected');
                this.isConnected = true;
                this.log('✅ VocalLabs WS connected');
            });
        });
        this.vocalLabsWebCall.onDisconnected(() => {
            this.ngZone.run(() => {
                this.updateStatus('Disconnected');
                this.updateBufferingStatus('No');
                this.isConnected = false;
                this.log('VocalLabs WS disconnected');
            });
        });
        this.vocalLabsWebCall.onError((err) => {
            this.ngZone.run(() => {
                this.log(`❌ VocalLabs error: ${err && err.message ? err.message : JSON.stringify(err)}`);
            });
        });
    }

    private getCallbacks() {
        return {
            onConnected: () => {
                this.ngZone.run(() => {
                    this.updateStatus('Connected');
                    this.isConnected = true;
                    this.log('RTVI Client connected successfully');
                    this.log('✅ Connection established - checking microphone access...');
                    this.checkMicrophoneAccess();
                });
            },
            onDisconnected: () => {
                this.ngZone.run(() => {
                    this.updateStatus('Disconnected');
                    this.updateBufferingStatus('No');
                    this.isConnected = false;
                    this.log('RTVI Client disconnected');
                });
            },
            onBotConnected: (participant: Participant) => {
                this.log(`Bot connected, timeTaken: ${Date.now() - this.startTime}`);
                this.log(`Bot participant: ${JSON.stringify(participant)}`);
            },
            onBotReady: (data) => {
                this.log(`Bot ready, timeTaken: ${Date.now() - this.startTime}`);
                this.log(`Bot ready data: ${JSON.stringify(data)}`);
                this.log('🤖 Bot is ready - ensuring audio capture continues...');
                this.setupMediaTracks();
                this.ensureAudioCaptureContinues();
                this.startManualAudioCapture();
                this.testAudioBufferingManually();

                // Restart audio buffering after bot is ready
                setTimeout(() => {
                    this.log('🔄 Restarting audio buffering after bot ready...');
                    this.restartAudioBufferingAfterBotReady();
                }, 1000);
            },
            onUserTranscript: (data) => {
                this.log(`User transcript received: ${JSON.stringify(data)}`);
                if (data.final) {
                    this.log(`🎤 User said: "${data.text}"`);
                } else {
                    this.log(`🎤 User speaking (interim): "${data.text}"`);
                }
            },
            onBotTranscript: (data) => {
                this.log(`🤖 Bot said: "${data.text}"`);
                this.log(`Bot transcript data: ${JSON.stringify(data)}`);
            },
            onMessageError: (error) => {
                this.log(`❌ Message error: ${error}`);
                console.error('Message error:', error);
            },
            onError: (error) => {
                this.log(`❌ RTVI Error: ${error}`);
                console.error('RTVI Error:', error);
            },
        };
    }

    private initializeRTVIClient(): void {
        try {
            this.log('Initializing RTVI Client...');
            this.log(`Received data: ${JSON.stringify(this.data)}`);

            // Fetch profile name from AppService
            let profileName = '';
            let email = '';
            let number = '';
            try {
                const account = this.appService.getCurrentAccount();
                console.log("Account fetched from appService - ", account);
                profileName = account.user.userMeta.name;
                email = account.user.email;
                number = account.user.mobile;
            } catch (e) {
                this.log('Could not fetch profile name from AppService');
            }
            console.log('Profile name from AppService:', profileName);

            // Prepare custom parameters from dialog data, but use profileName for client_name
            const customParams = {
                client_name: profileName,
                client_email: email,
                client_number: number,
                tts: this.data && this.data.tts || "elevenlabs",
                language: this.data && this.data.language || "en",
                voice: this.data && this.data.voice || "male",
                agent_id: this.data && this.data.agentId,
                entity_id: this.data && this.data.entityId,
                project_id: this.data && this.data.projectId,
                use_gemini: true,
                agent_type: this.agentType,
                custom_params: this.data && this.data.customParams || {}
            };

            this.log(`Custom params: ${JSON.stringify(customParams)}`);
            this.log(`Client name: ${customParams.client_name}`);
            this.log(`Agent ID: ${customParams.agent_id}`);
            this.log(`Project ID: ${customParams.project_id}`);

            // Build query parameters
            const queryParams = new URLSearchParams();
            Object.entries(customParams).forEach(([key, value]) => {
                if (value !== null && value !== undefined) {
                    if (typeof value === 'object') {
                        queryParams.append(key, JSON.stringify(value));
                    } else {
                        queryParams.append(key, String(value));
                    }
                }
            });

            const queryString = queryParams.toString();
            const baseUrl = queryString ? `${environment.pipecatAPI}/daily/connect?${queryString}` : `${environment.pipecatAPI}/daily/connect`;

            this.log(`Connection URL: ${baseUrl}`);

            const RTVIConfig: RTVIClientOptions = {
                transport: new DailyTransport({
                    bufferLocalAudioUntilBotReady: true,
                }),
                params: {
                    baseUrl: baseUrl,
                    endpoints: { connect: '/daily/connect' },
                },
                enableMic: true,
                enableCam: false,
                callbacks: this.getCallbacks()
            };

            this.log(`RTVI Config: ${JSON.stringify(RTVIConfig)}`);
            this.rtviClient = new RTVIClient(RTVIConfig);
            this.log('RTVI Client created successfully');

            // Register InstantVoiceHelper with enhanced configuration
            const instantVoiceHelper = new InstantVoiceHelper({
                callbacks: {
                    onAudioBufferingStarted: () => {
                        this.ngZone.run(() => {
                            try {
                                SoundUtils.beep();
                                this.log('🔊 Audio buffering started - microphone is capturing audio');
                            } catch (e) {
                                this.log(`Sound beep failed: ${e}`);
                            }
                            this.updateBufferingStatus('Yes');
                            this.log(`🎤 Audio buffering started, timeTaken: ${Date.now() - this.startTime}`);
                            this.log('🎤 User audio is being captured and sent to backend');
                        });
                    },
                    onAudioBufferingStopped: () => {
                        this.ngZone.run(() => {
                            this.updateBufferingStatus('No');
                            this.log(`🔇 Audio buffering stopped, timeTaken: ${Date.now() - this.startTime}`);
                            this.log('🔇 User audio capture has stopped');
                        });
                    },
                },
            });

            // Add debugging to the helper
            const originalHandleMessage = instantVoiceHelper.handleMessage.bind(instantVoiceHelper);
            instantVoiceHelper.handleMessage = (rtviMessage: any) => {
                this.log(`🎤 Helper received message: ${rtviMessage.type}`);
                originalHandleMessage(rtviMessage);
            };

            this.rtviClient.registerHelper('transport', instantVoiceHelper);
            this.log('InstantVoiceHelper registered');
            this.log('🎤 InstantVoiceHelper should now monitor audio buffering events');
            this.log('🎤 Helper message types: AUDIO_BUFFERING_STARTED, AUDIO_BUFFERING_STOPPED');

            // Store helper reference for manual testing
            this.instantVoiceHelper = instantVoiceHelper;

            this.setupTrackListeners();
            this.log('RTVI Client initialization completed');

            // Start periodic audio status monitoring
            this.startAudioStatusMonitoring();
        } catch (error) {
            this.log(`Error initializing RTVI Client: ${error}`);
            console.error('Error initializing RTVI Client:', error);
        }
    }

    private log(message: string): void {
        this.ngZone.run(() => {
            console.log(`[TestAgent] ${message}`);
            if (!this.debugLog || !this.debugLog.nativeElement) {
                console.warn('Debug log element not available');
                return;
            }

            try {
                const entry = document.createElement('div');
                entry.textContent = `${new Date().toISOString()} - ${message}`;
                if (message.startsWith('User: ')) {
                    entry.style.color = '#2196F3';
                } else if (message.startsWith('Bot: ')) {
                    entry.style.color = '#4CAF50';
                } else if (message.includes('Error')) {
                    entry.style.color = '#f44336';
                }
                this.debugLog.nativeElement.appendChild(entry);
                this.debugLog.nativeElement.scrollTop = this.debugLog.nativeElement.scrollHeight;
            } catch (error) {
                console.error('Error adding log entry:', error);
            }
        });
    }

    private updateStatus(status: string): void {
        try {
            if (this.statusSpan && this.statusSpan.nativeElement) {
                this.statusSpan.nativeElement.textContent = status;
            } else {
                this.log('Status span element not available');
            }
            this.log(`Status updated to: ${status}`);
        } catch (error) {
            this.log(`Error updating status: ${error}`);
        }
    }

    private updateBufferingStatus(status: string): void {
        try {
            if (this.bufferingAudioSpan && this.bufferingAudioSpan.nativeElement) {
                this.bufferingAudioSpan.nativeElement.textContent = status;
            } else {
                this.log('Buffering audio span element not available');
            }
            this.log(`Buffering status updated to: ${status}`);
            this.isAudioBuffering = status === 'Yes';
        } catch (error) {
            this.log(`Error updating buffering status: ${error}`);
        }
    }

    setupMediaTracks() {
        try {
            if (!this.rtviClient) {
                this.log('RTVI Client not available for media tracks setup');
                return;
            }
            const tracks = this.rtviClient.tracks();
            this.log(`📊 Available tracks: ${JSON.stringify(tracks)}`);

            if (tracks.bot && tracks.bot.audio) {
                this.log('🎵 Setting up bot audio track');
                this.setupAudioTrack(tracks.bot.audio);
            } else {
                this.log('⚠️ No bot audio tracks available');
            }

            // Log all available track properties for debugging
            this.log(`📊 Track object keys: ${Object.keys(tracks)}`);
            Object.keys(tracks).forEach(key => {
                this.log(`📊 Track ${key}: ${JSON.stringify(tracks[key])}`);
            });
        } catch (error) {
            this.log(`Error setting up media tracks: ${error}`);
        }
    }

    setupTrackListeners() {
        try {
            if (!this.rtviClient) {
                this.log('RTVI Client not available for track listeners setup');
                return;
            }

            this.rtviClient.on(RTVIEvent.TrackStarted, (track, participant) => {
                this.log(`🎵 Track started: ${track.kind} from ${participant ? participant.name : 'unknown'}`);
                this.log(`🎵 Track details: ${JSON.stringify(track)}`);
                this.log(`🎵 Participant details: ${JSON.stringify(participant)}`);

                if (participant && !participant.local && track.kind === 'audio') {
                    this.log('🎵 Setting up remote audio track');
                    this.setupAudioTrack(track);
                } else if (participant && participant.local && track.kind === 'audio') {
                    this.log('🎤 Local audio track started - user microphone active');
                    this.log(`🎤 Local audio track ID: ${track.id}`);
                    this.log(`🎤 Local audio track enabled: ${track.enabled}`);
                    this.log(`🎤 Local audio track muted: ${track.muted}`);
                }
            });

            this.rtviClient.on(RTVIEvent.TrackStopped, (track, participant) => {
                this.log(`🛑 Track stopped: ${track.kind} from ${(participant && participant.name) || 'unknown'}`);
                this.log(`🛑 Track ID: ${track.id}`);
            });

            this.log('✅ Track listeners setup completed');
        } catch (error) {
            this.log(`Error setting up track listeners: ${error}`);
        }
    }

    private setupAudioTrack(track: MediaStreamTrack): void {
        try {
            this.log('🎵 Setting up audio track');
            this.log(`🎵 Track ID: ${track.id}`);
            this.log(`🎵 Track kind: ${track.kind}`);
            this.log(`🎵 Track enabled: ${track.enabled}`);
            this.log(`🎵 Track muted: ${track.muted}`);
            this.log(`🎵 Track ready state: ${track.readyState}`);

            if (!this.botAudio || !this.botAudio.nativeElement) {
                this.log('❌ Audio element not available');
                return;
            }

            const audioElement = this.botAudio.nativeElement;
            if (
                audioElement.srcObject &&
                'getAudioTracks' in audioElement.srcObject
            ) {
                const mediaStream = audioElement.srcObject as MediaStream;
                const oldTrack = mediaStream.getAudioTracks()[0];
                if (oldTrack && oldTrack.id === track.id) {
                    this.log('🔄 Same audio track, skipping setup');
                    return;
                }
            }

            const newStream = new MediaStream([track]);
            audioElement.srcObject = newStream;
            this.log('✅ Audio track setup completed');
            this.log(`🎵 Audio element srcObject set: ${audioElement.srcObject ? 'Yes' : 'No'}`);

            // Add audio element event listeners for debugging
            audioElement.onloadedmetadata = () => {
                this.log('🎵 Audio metadata loaded');
            };

            audioElement.oncanplay = () => {
                this.log('🎵 Audio can play');
            };

            audioElement.onerror = (error) => {
                this.log(`❌ Audio element error: ${error}`);
            };

        } catch (error) {
            this.log(`Error setting up audio track: ${error}`);
        }
    }

    public async connect(): Promise<void> {
        this.isConnecting = true;
        try {
            this.startTime = Date.now();
            this.log('Connecting to bot...');
            if (this.useVocalLabsWs) {
                const wsUrl =
                    (this.data && this.data.vocallabsWsUrl) ||
                    (environment as any).vocallabsWsUrl ||
                    '';
                if (!wsUrl) {
                    this.log('❌ No VocalLabs WS URL provided (data.vocallabsWsUrl or environment.vocallabsWsUrl)');
                    this.updateStatus('Error');
                    return;
                }

                await this.vocalLabsWebCall.connect(wsUrl);

                // Route bot audio through <audio> element for existing visualizer
                const stream = this.vocalLabsWebCall.getOutputMediaStream();
                if (stream && this.botAudio && this.botAudio.nativeElement) {
                    this.botAudio.nativeElement.srcObject = stream;
                    try {
                        await this.botAudio.nativeElement.play();
                    } catch (e) { }
                }
            } else {
                if (!this.rtviClient) {
                    this.log('RTVI Client not available for connection');
                    return;
                }
                await this.rtviClient.connect();
            }
        } catch (error) {
            this.log(`Error connecting: ${(error as Error).message}`);
            this.updateStatus('Error');
            this.updateBufferingStatus('No');
            if (!this.useVocalLabsWs && this.rtviClient) {
                try {
                    await this.rtviClient.disconnect();
                } catch (disconnectError) {
                    this.log(`Error during disconnect: ${disconnectError}`);
                }
            }
        } finally {
            this.isConnecting = false;
        }
    }

    public async disconnect(): Promise<void> {
        this.isDisconnecting = true;
        try {
            this.log('Disconnecting from bot...');
            if (this.useVocalLabsWs) {
                await this.vocalLabsWebCall.disconnect();
            } else {
                if (!this.rtviClient) {
                    this.log('RTVI Client not available for disconnection');
                    return;
                }

                // Clear audio monitoring interval
                if (this.audioCheckInterval) {
                    clearInterval(this.audioCheckInterval);
                    this.log('📊 Audio monitoring interval cleared');
                }

                await this.rtviClient.disconnect();
            }

            if (this.botAudio && this.botAudio.nativeElement) {
                const audioElement = this.botAudio.nativeElement;
                if (
                    audioElement.srcObject &&
                    'getAudioTracks' in audioElement.srcObject
                ) {
                    const mediaStream = audioElement.srcObject as MediaStream;
                    mediaStream
                        .getAudioTracks()
                        .forEach((track) => track.stop());
                    audioElement.srcObject = null;
                }
            }
            this.log('Disconnection completed');
        } catch (error) {
            this.log(`Error disconnecting: ${(error as Error).message}`);
        } finally {
            this.isDisconnecting = false;
        }
    }

    close() {
        this.log('Closing dialog');

        // Disconnect if still connected
        if (this.isConnected && this.rtviClient) {
            this.disconnect().then(() => {
                this.dialogRef.close();
            }).catch((error) => {
                this.log(`Error during disconnect on close: ${error}`);
                this.dialogRef.close();
            });
        } else {
            this.dialogRef.close();
        }
    }

    private async checkMicrophoneAccess(): Promise<void> {
        try {
            this.log('🔍 Checking microphone access...');
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: true,
                video: false
            });
            this.log('✅ Microphone access granted');
            this.log(`📊 Audio tracks: ${stream.getAudioTracks().length}`);

            const audioTrack = stream.getAudioTracks()[0];
            if (audioTrack) {
                this.log(`🎤 Audio track settings: ${JSON.stringify(audioTrack.getSettings())}`);
                this.log(`🎤 Audio track constraints: ${JSON.stringify(audioTrack.getConstraints())}`);
                this.log(`🎤 Audio track enabled: ${audioTrack.enabled}`);
                this.log(`🎤 Audio track muted: ${audioTrack.muted}`);
                this.log(`🎤 Audio track ready state: ${audioTrack.readyState}`);
            }

            // Stop the test stream
            stream.getTracks().forEach(track => track.stop());
            this.log('✅ Microphone test completed');
        } catch (error) {
            this.log(`❌ Microphone access denied: ${error}`);
            console.error('Microphone access error:', error);
        }
    }

    private ensureAudioCaptureContinues(): void {
        try {
            this.log('🔍 Checking if audio capture is still active...');

            // Check if we have active local audio tracks
            if (this.rtviClient) {
                const tracks = this.rtviClient.tracks();
                this.log(`📊 Current tracks after bot ready: ${JSON.stringify(tracks)}`);

                // Check if local audio track is still active
                if (tracks.local && tracks.local.audio) {
                    const audioTrack = tracks.local.audio;
                    this.log(`🎤 Local audio track status: enabled=${audioTrack.enabled}, muted=${audioTrack.muted}, readyState=${audioTrack.readyState}`);

                    if (!audioTrack.enabled || audioTrack.muted) {
                        this.log('⚠️ Local audio track is disabled or muted - attempting to re-enable...');
                        audioTrack.enabled = true;
                        this.log('✅ Local audio track re-enabled');
                    }
                } else {
                    this.log('⚠️ No local audio track found - this might indicate audio capture has stopped');
                }
            }

            // Set a timer to check audio status periodically
            setTimeout(() => {
                if (this.isConnected && !this.isAudioBuffering) {
                    this.log('⚠️ Audio buffering stopped - attempting to restart...');
                    this.restartAudioCapture();
                }
            }, 5000); // Check after 5 seconds

        } catch (error) {
            this.log(`Error ensuring audio capture continues: ${error}`);
        }
    }

    private restartAudioCapture(): void {
        try {
            this.log('🔄 Attempting to restart audio capture...');

            if (this.rtviClient) {
                // Try to re-enable microphone
                this.rtviClient.enableMic(true);
                this.log('✅ Microphone re-enabled');

                // Check tracks again
                const tracks = this.rtviClient.tracks();
                if (tracks.local && tracks.local.audio) {
                    tracks.local.audio.enabled = true;
                    this.log('✅ Local audio track re-enabled');
                }
            }
        } catch (error) {
            this.log(`Error restarting audio capture: ${error}`);
        }
    }

    private startManualAudioCapture(): void {
        try {
            this.log('🎤 Starting manual audio capture...');

            if (this.rtviClient) {
                // Ensure microphone is enabled
                this.rtviClient.enableMic(true);
                this.log('✅ Microphone enabled');

                // Get current tracks and ensure local audio is active
                const tracks = this.rtviClient.tracks();
                this.log(`📊 Current tracks for manual capture: ${JSON.stringify(tracks)}`);

                // Check if we have local audio track
                if (tracks.local && tracks.local.audio) {
                    const audioTrack = tracks.local.audio;
                    this.log(`🎤 Local audio track found: enabled=${audioTrack.enabled}, muted=${audioTrack.muted}`);

                    // Ensure the track is enabled
                    if (!audioTrack.enabled) {
                        audioTrack.enabled = true;
                        this.log('✅ Local audio track enabled');
                    }

                    // Add event listeners to the audio track
                    audioTrack.onended = () => {
                        this.log('⚠️ Local audio track ended');
                    };

                    audioTrack.onmute = () => {
                        this.log('🔇 Local audio track muted');
                    };

                    audioTrack.onunmute = () => {
                        this.log('🔊 Local audio track unmuted');
                    };

                    // Manually trigger audio buffering started
                    this.log('🎤 Manually triggering audio buffering started...');
                    this.updateBufferingStatus('Yes');
                    this.log('🎤 Manual audio capture started - should now be sending to backend');

                } else {
                    this.log('⚠️ No local audio track found for manual capture');
                }

                // Also try to get user media directly to ensure audio is being captured
                this.ensureUserMediaAccess();
            }
        } catch (error) {
            this.log(`Error starting manual audio capture: ${error}`);
        }
    }

    private async ensureUserMediaAccess(): Promise<void> {
        try {
            this.log('🎤 Ensuring user media access for audio capture...');
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true
                },
                video: false
            });

            this.log('✅ User media access confirmed');
            this.log(`📊 Active audio tracks: ${stream.getAudioTracks().length}`);

            const audioTrack = stream.getAudioTracks()[0];
            if (audioTrack) {
                this.log(`🎤 Audio track active: enabled=${audioTrack.enabled}, muted=${audioTrack.muted}, readyState=${audioTrack.readyState}`);

                // Keep the stream active for a short time to ensure audio is being captured
                setTimeout(() => {
                    this.log('🎤 Audio capture test completed - stream should be active');
                    // Don't stop the stream immediately, let it continue
                }, 2000);
            }
        } catch (error) {
            this.log(`Error ensuring user media access: ${error}`);
        }
    }

    private startAudioStatusMonitoring(): void {
        try {
            this.log('📊 Starting periodic audio status monitoring...');

            // Check audio status every 3 seconds
            const audioCheckInterval = setInterval(() => {
                if (!this.isConnected) {
                    clearInterval(audioCheckInterval);
                    this.log('📊 Audio monitoring stopped - disconnected');
                    return;
                }

                this.log('📊 Periodic audio status check...');

                if (this.rtviClient) {
                    const tracks = this.rtviClient.tracks();
                    this.log(`📊 Current tracks: ${Object.keys(tracks).join(', ')}`);

                    // Check local audio track status
                    if (tracks.local && tracks.local.audio) {
                        const audioTrack = tracks.local.audio;
                        this.log(`🎤 Local audio: enabled=${audioTrack.enabled}, muted=${audioTrack.muted}, readyState=${audioTrack.readyState}`);

                        if (!audioTrack.enabled) {
                            this.log('⚠️ Local audio track is disabled - re-enabling...');
                            audioTrack.enabled = true;
                        }
                    } else {
                        this.log('⚠️ No local audio track found');
                    }

                    // Check if audio buffering is active
                    if (!this.isAudioBuffering) {
                        this.log('⚠️ Audio buffering is not active - attempting to restart...');
                        this.updateBufferingStatus('Yes');
                    } else {
                        this.log('✅ Audio buffering is active');
                    }
                }
            }, 3000);

            // Store the interval for cleanup
            this.audioCheckInterval = audioCheckInterval;

        } catch (error) {
            this.log(`Error starting audio status monitoring: ${error}`);
        }
    }

    private testAudioBufferingManually(): void {
        try {
            this.log('🎤 Starting manual audio buffering test...');

            if (this.rtviClient) {
                // Ensure microphone is enabled
                this.rtviClient.enableMic(true);
                this.log('✅ Microphone enabled');

                // Get current tracks and ensure local audio is active
                const tracks = this.rtviClient.tracks();
                this.log(`📊 Current tracks for manual buffering test: ${JSON.stringify(tracks)}`);

                // Check if we have local audio track
                if (tracks.local && tracks.local.audio) {
                    const audioTrack = tracks.local.audio;
                    this.log(`🎤 Local audio track found: enabled=${audioTrack.enabled}, muted=${audioTrack.muted}`);

                    // Ensure the track is enabled
                    if (!audioTrack.enabled) {
                        audioTrack.enabled = true;
                        this.log('✅ Local audio track enabled');
                    }

                    // Add event listeners to the audio track
                    audioTrack.onended = () => {
                        this.log('⚠️ Local audio track ended');
                    };

                    audioTrack.onmute = () => {
                        this.log('🔇 Local audio track muted');
                    };

                    audioTrack.onunmute = () => {
                        this.log('🔊 Local audio track unmuted');
                    };

                    // Manually trigger audio buffering started
                    this.log('🎤 Manually triggering audio buffering started...');
                    this.updateBufferingStatus('Yes');
                    this.log('🎤 Manual audio buffering test started - should now be sending to backend');

                    // Manually send audio buffering message to helper
                    this.triggerAudioBufferingEvent(true);

                } else {
                    this.log('⚠️ No local audio track found for manual buffering test');
                }

                // Also try to get user media directly to ensure audio is being captured
                this.ensureUserMediaAccess();
            }
        } catch (error) {
            this.log(`Error starting manual audio buffering test: ${error}`);
        }
    }

    private triggerAudioBufferingEvent(started: boolean): void {
        try {
            if (this.instantVoiceHelper) {
                const messageType = started ? DailyRTVIMessageType.AUDIO_BUFFERING_STARTED : DailyRTVIMessageType.AUDIO_BUFFERING_STOPPED;
                const message = {
                    type: messageType,
                    id: `manual-${Date.now()}`,
                    label: started ? 'Audio Buffering Started' : 'Audio Buffering Stopped',
                    data: {},
                    severity: 'info'
                };
                this.log(`🎤 Manually sending ${messageType} message to helper`);
                this.instantVoiceHelper.handleMessage(message);
            } else {
                this.log('⚠️ InstantVoiceHelper not available for manual trigger');
            }
        } catch (error) {
            this.log(`Error triggering audio buffering event: ${error}`);
        }
    }

    private restartAudioBufferingAfterBotReady(): void {
        try {
            this.log('🔄 Restarting audio buffering after bot ready...');
            if (this.rtviClient) {
                this.rtviClient.enableMic(true);
                this.log('✅ Microphone re-enabled');
            }
        } catch (error) {
            this.log(`Error restarting audio buffering after bot ready: ${error}`);
        }
    }
}